import React, { useState } from 'react';
import { Avatar, Box, ListItemText, Menu, MenuItem, Tooltip } from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { colorFor, initialsFor, MEMBER_ROLE_LABELS, sortMembers } from './memberRoles';

/**
 * Finds which bet member currently owns a scope or todo.
 *
 * A Jira-backed item identifies its assignee by Atlassian accountId; a manual
 * item by our own userId. Either can fall back to matching the stored display
 * name, which covers a Jira assignee whose accountId has not been cached onto a
 * member yet.
 */
export const matchAssignee = (target, members = []) => {
  if (!target) return null;
  const byAccount = target.assigneeAccountId
    ? members.find(m => m.jiraAccountId && m.jiraAccountId === target.assigneeAccountId)
    : null;
  if (byAccount) return byAccount;
  const byUser = target.assigneeUserId
    ? members.find(m => m.userId === target.assigneeUserId)
    : null;
  if (byUser) return byUser;
  const byName = target.assigneeName
    ? members.find(m => m.name && m.name === target.assigneeName)
    : null;
  return byName || null;
};

/**
 * An avatar button that opens a menu of the bet's members to own a scope or a
 * todo. Calls onAssign(userId | null); the caller persists it (to Jira or
 * locally) and reverts on failure.
 *
 * Read-only when no onAssign is given: it renders just the current assignee's
 * avatar, or nothing when nobody owns the item, so a reviewer's board stays
 * quiet rather than sprouting empty controls.
 */
const AssigneePicker = ({
  target,
  members = [],
  onAssign,
  size = 24,
  disabled = false,
}) => {
  const [anchor, setAnchor] = useState(null);
  const current = matchAssignee(target, members);
  const label = current?.name || target?.assigneeName || null;
  const readOnly = !onAssign;

  if (readOnly && !label) return null;

  const avatar = current || label ? (
    <Avatar
      sx={{ width: size, height: size, fontSize: size * 0.42, bgcolor: colorFor(current?.userId || label) }}
    >
      {initialsFor(label)}
    </Avatar>
  ) : (
    <Avatar sx={{ width: size, height: size, bgcolor: 'transparent', color: 'text.disabled', border: '1px dashed', borderColor: 'divider' }}>
      <PersonOutlineIcon sx={{ fontSize: size * 0.62 }} />
    </Avatar>
  );

  const tooltip = label
    ? `${label}${current?.role && current.role !== 'member' ? ` · ${MEMBER_ROLE_LABELS[current.role] || current.role}` : ''}`
    : 'Unassigned';

  if (readOnly) {
    return <Tooltip title={tooltip}><Box sx={{ display: 'inline-flex' }}>{avatar}</Box></Tooltip>;
  }

  const choose = (userId) => {
    setAnchor(null);
    // A no-op pick (same person, or clearing an already-empty slot) skips the
    // write so the menu just closes.
    const currentUserId = current?.userId || null;
    if ((userId || null) === currentUserId) return;
    onAssign(userId);
  };

  return (
    <>
      <Tooltip title={tooltip}>
        <Box
          component="button"
          type="button"
          disabled={disabled}
          onClick={(e) => setAnchor(e.currentTarget)}
          sx={{
            p: 0,
            border: 'none',
            background: 'none',
            cursor: disabled ? 'default' : 'pointer',
            display: 'inline-flex',
            opacity: disabled ? 0.5 : 1,
          }}
          aria-label="Assign"
        >
          {avatar}
        </Box>
      </Tooltip>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        <MenuItem selected={!current} onClick={() => choose(null)}>
          <ListItemText primary="Unassigned" />
        </MenuItem>
        {sortMembers(members).map(member => (
          <MenuItem
            key={member.userId}
            selected={current?.userId === member.userId}
            onClick={() => choose(member.userId)}
          >
            <Avatar sx={{ width: 22, height: 22, fontSize: 10, mr: 1, bgcolor: colorFor(member.userId || member.name) }}>
              {initialsFor(member.name)}
            </Avatar>
            <ListItemText
              primary={member.name || 'Unknown'}
              secondary={member.role && member.role !== 'member'
                ? (MEMBER_ROLE_LABELS[member.role] || member.role)
                : null}
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default AssigneePicker;
